import { useMemo, useState } from 'react';
import { ArrowUpDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import i18n from '@/utils/i18n';
import { Routes } from '@/utils/routes';
import { DataTableFacetedFilter } from '@/views/Order/components/table/TableFacetedFilter';

import type { EventParticipant } from './types';

type SortKey = 'name' | 'eventAccount' | 'deliveryAddress';
type SortDirection = 'asc' | 'desc';

export function EventItemLocationsTab({
  eventParticipants,
}: {
  eventParticipants?: EventParticipant[] | null;
}) {
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedItemLocationId, setSelectedItemLocationId] = useState<
    string | null
  >(null);
  const [selectedEventAccountId, setSelectedEventAccountId] = useState<
    string | null
  >(null);
  const [selectedDeliveryAddressId, setSelectedDeliveryAddressId] = useState<
    string | null
  >(null);

  const allRows = useMemo(
    () =>
      eventParticipants?.flatMap((participant) =>
        (participant.item_locations || []).map((location) => ({
          id: location.id,
          name: location.name || '-',
          eventAccountId: participant.id,
          eventAccountName: participant.name || '-',
          deliveryAddressId: location.delivery_address?.id || '__none__',
          deliveryAddressName: location.delivery_address?.name || '-',
        })),
      ) || [],
    [eventParticipants],
  );

  const itemLocationOptions = useMemo(
    () =>
      [...allRows]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((row) => ({
          value: row.id,
          getLabel: () => row.name,
        })),
    [allRows],
  );

  const eventAccountOptions = useMemo(
    () =>
      Array.from(
        new Map(
          allRows.map((row) => [
            row.eventAccountId,
            { value: row.eventAccountId, name: row.eventAccountName },
          ]),
        ).values(),
      )
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((o) => ({ value: o.value, getLabel: () => o.name })),
    [allRows],
  );

  const deliveryAddressOptions = useMemo(
    () =>
      Array.from(
        new Map(
          allRows.map((row) => [
            row.deliveryAddressId,
            { value: row.deliveryAddressId, name: row.deliveryAddressName },
          ]),
        ).values(),
      )
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((o) => ({ value: o.value, getLabel: () => o.name })),
    [allRows],
  );

  const itemLocations = useMemo(() => {
    const filteredRows = allRows.filter((row) => {
      if (selectedItemLocationId && row.id !== selectedItemLocationId) {
        return false;
      }
      if (
        selectedEventAccountId &&
        row.eventAccountId !== selectedEventAccountId
      ) {
        return false;
      }
      if (
        selectedDeliveryAddressId &&
        row.deliveryAddressId !== selectedDeliveryAddressId
      ) {
        return false;
      }
      return true;
    });

    filteredRows.sort((a, b) => {
      if (sortKey === 'name') {
        const aName = a.name.toLocaleLowerCase();
        const bName = b.name.toLocaleLowerCase();
        return sortDirection === 'asc'
          ? aName.localeCompare(bName)
          : bName.localeCompare(aName);
      }

      const aAccount = a.eventAccountName.toLocaleLowerCase();
      const bAccount = b.eventAccountName.toLocaleLowerCase();
      if (sortKey === 'eventAccount') {
        return sortDirection === 'asc'
          ? aAccount.localeCompare(bAccount)
          : bAccount.localeCompare(aAccount);
      }

      const aDeliveryAddress = a.deliveryAddressName.toLocaleLowerCase();
      const bDeliveryAddress = b.deliveryAddressName.toLocaleLowerCase();
      return sortDirection === 'asc'
        ? aDeliveryAddress.localeCompare(bDeliveryAddress)
        : bDeliveryAddress.localeCompare(aDeliveryAddress);
    });

    return filteredRows;
  }, [
    allRows,
    selectedDeliveryAddressId,
    selectedEventAccountId,
    selectedItemLocationId,
    sortDirection,
    sortKey,
  ]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDirection('asc');
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-6">
      <div className="tw:space-y-2">
        <h1 className="tw:text-4xl tw:font-extrabold">
          {i18n.t('Event:itemLocations')}
        </h1>
      </div>

      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <DataTableFacetedFilter
          title={i18n.t('Common:name')}
          options={itemLocationOptions}
          value={selectedItemLocationId ?? '__all__'}
          onValueChange={(value) => {
            setSelectedItemLocationId(value === '__all__' ? null : value);
          }}
        />
        <DataTableFacetedFilter
          title={i18n.t('Event:eventAccount')}
          options={eventAccountOptions}
          value={selectedEventAccountId ?? '__all__'}
          onValueChange={(value) => {
            setSelectedEventAccountId(value === '__all__' ? null : value);
          }}
        />
        <DataTableFacetedFilter
          title={i18n.t('Events:deliveryAddressesTitle')}
          options={deliveryAddressOptions}
          value={selectedDeliveryAddressId ?? '__all__'}
          onValueChange={(value) => {
            setSelectedDeliveryAddressId(value === '__all__' ? null : value);
          }}
        />
      </div>

      <Separator />

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="tw:w-1/3">
              <Button
                variant="ghost"
                size="sm"
                className="tw:-ml-2"
                onClick={() => handleSort('name')}
              >
                {i18n.t('Common:name')}
                <ArrowUpDown className="tw:size-4" />
              </Button>
            </TableHead>
            <TableHead className="tw:w-1/3">
              <Button
                variant="ghost"
                size="sm"
                className="tw:-ml-2"
                onClick={() => handleSort('eventAccount')}
              >
                {i18n.t('Event:eventAccount')}
                <ArrowUpDown className="tw:size-4" />
              </Button>
            </TableHead>
            <TableHead className="tw:w-1/3">
              <Button
                variant="ghost"
                size="sm"
                className="tw:-ml-2"
                onClick={() => handleSort('deliveryAddress')}
              >
                {i18n.t('Events:deliveryAddressesTitle')}
                <ArrowUpDown className="tw:size-4" />
              </Button>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {itemLocations.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="tw:text-muted-foreground">
                {i18n.t('Event:noItemlocation')}
              </TableCell>
            </TableRow>
          ) : (
            itemLocations.map((location) => (
              <TableRow key={`${location.id}-${location.eventAccountName}`}>
                <TableCell className="tw:max-w-20 tw:truncate">
                  <Link
                    to={Routes.itemLocation(location.id)}
                    className="tw:underline tw:hover:text-lilac-700"
                  >
                    {location.name}
                  </Link>
                </TableCell>
                <TableCell>{location.eventAccountName}</TableCell>
                <TableCell className="tw:max-w-20 tw:truncate">
                  {location.deliveryAddressName}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
